import { useState } from 'react'
import { ActivityIndicator, Pressable, Text, TextInput, View } from 'react-native'

import { theme } from '../../../../theme'
import { AGE_GATE_COPY, AGE_GATE_TEST_IDS } from '../../constants/ageGate.constants'
import {
  formatDateOfBirthForConfirmation,
  validateDateOfBirthParts,
  type DateOfBirthParts,
} from '../../helpers/dateOfBirth.helper'
import type { AgeGateSubmitError } from '../../hooks/useAgeVerification'
import { ageVerificationPromptStyles as styles } from './AgeVerificationPrompt.styles'

export interface AgeVerificationPromptProps {
  mode: 'prompt' | 'blocked'
  isSubmitting?: boolean
  submitError?: AgeGateSubmitError
  onSubmit: (parts: DateOfBirthParts) => void
  onSignOut: () => void
}

const digitsOnly = (value: string, maxLength: number) =>
  value.replace(/\D/g, '').slice(0, maxLength)

/**
 * Presentational age-gate content: the date-of-birth prompt, a confirmation step, or the
 * under-18 blocked view. Rendered inside a modal by AgeVerificationGate; no data fetching.
 *
 * The confirmation step exists because the date is written once: a typo that reads as
 * under 18 locks the account and the device, so the user sees the date spelled out and
 * agrees to it before anything is saved.
 */
export function AgeVerificationPrompt({
  mode,
  isSubmitting = false,
  submitError = null,
  onSubmit,
  onSignOut,
}: AgeVerificationPromptProps) {
  const [parts, setParts] = useState<DateOfBirthParts>({ day: '', month: '', year: '' })
  const [localError, setLocalError] = useState<AgeGateSubmitError>(null)
  const [confirming, setConfirming] = useState<string | null>(null)

  if (mode === 'blocked') {
    return (
      <View style={styles.sheet} testID={AGE_GATE_TEST_IDS.BLOCKED}>
        <Text style={styles.title} accessibilityRole="header">
          {AGE_GATE_COPY.BLOCKED_TITLE}
        </Text>
        <Text style={styles.body}>{AGE_GATE_COPY.BLOCKED_BODY}</Text>
        <Text style={styles.body}>{AGE_GATE_COPY.BLOCKED_SUPPORT}</Text>
        <Pressable
          accessibilityRole="button"
          style={styles.secondaryButton}
          onPress={onSignOut}
          testID={AGE_GATE_TEST_IDS.SIGN_OUT}
        >
          <Text style={styles.secondaryButtonText}>{AGE_GATE_COPY.SIGN_OUT}</Text>
        </Pressable>
      </View>
    )
  }

  if (confirming !== null) {
    return (
      <View style={styles.sheet} testID={AGE_GATE_TEST_IDS.CONFIRM_VIEW}>
        <Text style={styles.title} accessibilityRole="header">
          {AGE_GATE_COPY.CONFIRM_TITLE}
        </Text>
        <Text style={styles.confirmDate} testID={AGE_GATE_TEST_IDS.CONFIRM_DATE}>
          {formatDateOfBirthForConfirmation(confirming)}
        </Text>
        <Text style={styles.body}>{AGE_GATE_COPY.CONFIRM_BODY}</Text>
        {submitError === 'failed' ? (
          <Text style={styles.error} testID={AGE_GATE_TEST_IDS.ERROR} accessibilityRole="alert">
            {AGE_GATE_COPY.SUBMIT_FAILED}
          </Text>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: isSubmitting }}
          disabled={isSubmitting}
          style={[styles.primaryButton, isSubmitting && styles.primaryButtonDisabled]}
          onPress={() => onSubmit(parts)}
          testID={AGE_GATE_TEST_IDS.CONFIRM}
        >
          {isSubmitting ? (
            <ActivityIndicator size="small" color={theme.white} />
          ) : (
            <Text style={styles.primaryButtonText}>{AGE_GATE_COPY.CONFIRM}</Text>
          )}
        </Pressable>
        <Pressable
          accessibilityRole="button"
          disabled={isSubmitting}
          style={styles.secondaryButton}
          onPress={() => setConfirming(null)}
          testID={AGE_GATE_TEST_IDS.EDIT}
        >
          <Text style={styles.secondaryButtonText}>{AGE_GATE_COPY.EDIT}</Text>
        </Pressable>
      </View>
    )
  }

  const isComplete = parts.day.length > 0 && parts.month.length > 0 && parts.year.length === 4
  const canContinue = isComplete && !isSubmitting
  const shownError = localError ?? submitError
  const errorMessage =
    shownError === 'invalid'
      ? AGE_GATE_COPY.INVALID_DATE
      : shownError === 'failed'
        ? AGE_GATE_COPY.SUBMIT_FAILED
        : null

  const handleContinue = () => {
    const validation = validateDateOfBirthParts(parts)
    if (!validation.ok) {
      setLocalError('invalid')
      return
    }
    setLocalError(null)
    setConfirming(validation.isoDate)
  }

  const renderField = (
    key: keyof DateOfBirthParts,
    label: string,
    placeholder: string,
    maxLength: number,
    testID: string
  ) => (
    <View style={key === 'year' ? styles.fieldYear : styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        value={parts[key]}
        onChangeText={(value) => {
          setLocalError(null)
          setParts((prev) => ({ ...prev, [key]: digitsOnly(value, maxLength) }))
        }}
        placeholder={placeholder}
        placeholderTextColor={theme.gray[500]}
        keyboardType="number-pad"
        inputMode="numeric"
        maxLength={maxLength}
        editable={!isSubmitting}
        accessibilityLabel={label}
        style={[styles.input, isSubmitting && styles.inputDisabled]}
        testID={testID}
      />
    </View>
  )

  return (
    <View style={styles.sheet} testID={AGE_GATE_TEST_IDS.PROMPT}>
      <Text style={styles.title} accessibilityRole="header">
        {AGE_GATE_COPY.PROMPT_TITLE}
      </Text>
      <Text style={styles.body}>{AGE_GATE_COPY.PROMPT_BODY}</Text>

      <View style={styles.fieldsRow}>
        {renderField('day', AGE_GATE_COPY.DAY_LABEL, AGE_GATE_COPY.DAY_PLACEHOLDER, 2, AGE_GATE_TEST_IDS.DAY)}
        {renderField('month', AGE_GATE_COPY.MONTH_LABEL, AGE_GATE_COPY.MONTH_PLACEHOLDER, 2, AGE_GATE_TEST_IDS.MONTH)}
        {renderField('year', AGE_GATE_COPY.YEAR_LABEL, AGE_GATE_COPY.YEAR_PLACEHOLDER, 4, AGE_GATE_TEST_IDS.YEAR)}
      </View>

      {errorMessage ? (
        <Text style={styles.error} testID={AGE_GATE_TEST_IDS.ERROR} accessibilityRole="alert">
          {errorMessage}
        </Text>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canContinue }}
        disabled={!canContinue}
        style={[styles.primaryButton, !canContinue && styles.primaryButtonDisabled]}
        onPress={handleContinue}
        testID={AGE_GATE_TEST_IDS.CONTINUE}
      >
        <Text style={styles.primaryButtonText}>{AGE_GATE_COPY.CONTINUE}</Text>
      </Pressable>
    </View>
  )
}
